import type { AgencyBrandingPayload, Headshot as AgencyBrandingApiLogo } from '../../contracts/reporting-api/document/agency-branding';
import type { AgencyBrandingResponse } from '../../contracts/reporting-api/response/agency-branding';
import { Headshot, HeadshotKeys } from './headshot.model';
import { ImageCropperPayload } from './image-cropper.model';

export enum AgencyBrandingKeys {
  bannerColor = 'bannerColor',
  bulletinTitleColor = 'bulletinTitleColor',
  reportTitleColor = 'reportTitleColor',
  tableTextColor = 'tableTextColor',
  logo = 'logo',
}

export interface AgencyBranding {
  [AgencyBrandingKeys.bannerColor]?: string;
  [AgencyBrandingKeys.bulletinTitleColor]?: string;
  [AgencyBrandingKeys.reportTitleColor]?: string;
  [AgencyBrandingKeys.tableTextColor]?: string;
  [AgencyBrandingKeys.logo]?: Headshot;
}

const EMPTY_BRANDING_LOGO: Headshot = {
  [HeadshotKeys.wasabiPath]: '',
};

/** Alliance Group report defaults used when agency branding is not saved yet. Wiil be changed after API is ready */
export const AGENCY_BRANDING_DEFAULTS: Required<AgencyBranding> = {
  [AgencyBrandingKeys.bannerColor]: '#091d48',
  [AgencyBrandingKeys.bulletinTitleColor]: '#ffffff',
  [AgencyBrandingKeys.reportTitleColor]: '#289FE7',
  [AgencyBrandingKeys.tableTextColor]: '#091d48',
  [AgencyBrandingKeys.logo]: EMPTY_BRANDING_LOGO,
};

const toRelativeCropperValue = (value: number | string | null | undefined): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return String(value);
};

const toApiRelativeCropperValue = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return null;
  }

  return numericValue;
};

const createImageCropperPayload = (
  source?: AgencyBrandingApiLogo['imageCropperPayload'] | ImageCropperPayload | null,
): ImageCropperPayload | undefined => {
  const relativeCropperPosition = source?.relativeCropperPosition;
  if (!relativeCropperPosition) {
    return undefined;
  }

  const relativeX1 = toRelativeCropperValue(relativeCropperPosition.relativeX1);
  const relativeX2 = toRelativeCropperValue(relativeCropperPosition.relativeX2);
  const relativeY1 = toRelativeCropperValue(relativeCropperPosition.relativeY1);
  const relativeY2 = toRelativeCropperValue(relativeCropperPosition.relativeY2);

  if (!relativeX1 || !relativeX2 || !relativeY1 || !relativeY2) {
    return undefined;
  }

  return {
    relativeCropperPosition: {
      relativeX1,
      relativeX2,
      relativeY1,
      relativeY2,
    },
  };
};

const createApiImageCropperPayload = (
  source?: ImageCropperPayload | AgencyBrandingApiLogo['imageCropperPayload'] | null,
): AgencyBrandingApiLogo['imageCropperPayload'] => {
  const relativeCropperPosition = source?.relativeCropperPosition;
  if (!relativeCropperPosition) {
    return null;
  }

  const relativeX1 = toApiRelativeCropperValue(relativeCropperPosition.relativeX1);
  const relativeX2 = toApiRelativeCropperValue(relativeCropperPosition.relativeX2);
  const relativeY1 = toApiRelativeCropperValue(relativeCropperPosition.relativeY1);
  const relativeY2 = toApiRelativeCropperValue(relativeCropperPosition.relativeY2);

  if (relativeX1 === null || relativeX2 === null || relativeY1 === null || relativeY2 === null) {
    return null;
  }

  return {
    relativeCropperPosition: {
      relativeX1,
      relativeX2,
      relativeY1,
      relativeY2,
    },
  };
};

type AgencyBrandingLogoSource = AgencyBrandingApiLogo | Headshot | string | null | undefined;

/**
 * Normalizes branding logo to UI Headshot shape.
 * Accepts API logo object, UI Headshot, or legacy string wasabi paths.
 */
export const createAgencyBrandingLogo = (source?: AgencyBrandingLogoSource): Headshot => {
  if (!source) {
    return { ...EMPTY_BRANDING_LOGO };
  }

  if (typeof source === 'string') {
    return {
      [HeadshotKeys.wasabiPath]: source,
    };
  }

  const wasabiPath = source.wasabiPath ?? '';
  const imageCropperPayload = createImageCropperPayload(source.imageCropperPayload);

  if (!imageCropperPayload) {
    return {
      [HeadshotKeys.wasabiPath]: wasabiPath,
    };
  }

  return {
    [HeadshotKeys.wasabiPath]: wasabiPath,
    [HeadshotKeys.imageCropperPayload]: imageCropperPayload,
  };
};

/**
 * Maps reporting API agency branding payload into UI AgencyBranding model.
 */
export const mapAgencyBrandingFromApi = (source: AgencyBrandingResponse): AgencyBranding => ({
  [AgencyBrandingKeys.bannerColor]: source.bannerColor ?? undefined,
  [AgencyBrandingKeys.bulletinTitleColor]: source.bulletinTitleColor ?? undefined,
  [AgencyBrandingKeys.reportTitleColor]: source.reportTitleColor ?? undefined,
  [AgencyBrandingKeys.tableTextColor]: source.tableTextColor ?? undefined,
  [AgencyBrandingKeys.logo]: createAgencyBrandingLogo(source.logo),
});

/**
 * Maps UI AgencyBranding model into reporting API update payload.
 */
export const mapAgencyBrandingToApi = (source: AgencyBranding): AgencyBrandingPayload => {
  const logo = createAgencyBrandingLogo(source[AgencyBrandingKeys.logo]);
  const wasabiPath = logo[HeadshotKeys.wasabiPath]?.trim() ?? '';

  return {
    [AgencyBrandingKeys.bannerColor]: source[AgencyBrandingKeys.bannerColor] ?? null,
    [AgencyBrandingKeys.bulletinTitleColor]: source[AgencyBrandingKeys.bulletinTitleColor] ?? null,
    [AgencyBrandingKeys.reportTitleColor]: source[AgencyBrandingKeys.reportTitleColor] ?? null,
    [AgencyBrandingKeys.tableTextColor]: source[AgencyBrandingKeys.tableTextColor] ?? null,
    [AgencyBrandingKeys.logo]: wasabiPath
      ? {
          wasabiPath,
          imageCropperPayload: createApiImageCropperPayload(logo[HeadshotKeys.imageCropperPayload]) ?? null,
        }
      : null,
  };
};

/**
 * Resolves branding for UI/preview.
 * Colors fall back to AG defaults; logo stays empty unless agency branding has one.
 */
export const createAgencyBranding = (source?: AgencyBranding | null): Required<AgencyBranding> => ({
  [AgencyBrandingKeys.bannerColor]:
    source?.[AgencyBrandingKeys.bannerColor] || AGENCY_BRANDING_DEFAULTS[AgencyBrandingKeys.bannerColor],
  [AgencyBrandingKeys.bulletinTitleColor]:
    source?.[AgencyBrandingKeys.bulletinTitleColor] ||
    AGENCY_BRANDING_DEFAULTS[AgencyBrandingKeys.bulletinTitleColor],
  [AgencyBrandingKeys.reportTitleColor]:
    source?.[AgencyBrandingKeys.reportTitleColor] || AGENCY_BRANDING_DEFAULTS[AgencyBrandingKeys.reportTitleColor],
  [AgencyBrandingKeys.tableTextColor]:
    source?.[AgencyBrandingKeys.tableTextColor] || AGENCY_BRANDING_DEFAULTS[AgencyBrandingKeys.tableTextColor],
  [AgencyBrandingKeys.logo]: createAgencyBrandingLogo(source?.[AgencyBrandingKeys.logo]),
});
